import { TrackingHandler } from './TrackingHandler';
import { ServerSideGtmListener } from './listeners/ServerSideGtmListener';
const formatDisplayNumber = (fullNumber) => {
    const numberElements = fullNumber.split(' ');
    if (numberElements.length !== 2) {
        return fullNumber;
    }
    const prefix = numberElements[0];
    let number = numberElements[1];
    switch (number.length) {
        case 5:
            number = number.substring(0, 2) + ' ' + number.substring(2);
            break;
        case 6:
        case 7:
            number = number.substring(0, 3) + ' ' + number.substring(3);
            break;
        case 8:
            number = number.substring(0, 4) + ' ' + number.substring(4);
            break;
        case 9:
            number = number.substring(0, 3) + ' ' + number.substring(3, 6) + ' ' + number.substring(6, 9);
            break;
        case 10:
        case 11:
        case 12:
            number = number.substring(0, 4) + ' ' + number.substring(4, 8) + ' ' + number.substring(8);
            break;
    }
    return prefix + ' ' + number;
};
export const initPhoneTracking = (gaClientId) => {
    initNumbers('a.js-phone-number--telephone', 'telephone', gaClientId);
    initNumbers('a.js-phone-number--business-address, a.js-phone-number--conference-room, a.js-phone-number--office', 'office', gaClientId);
};
const createNewNumber = async (trackingAccount, trackingData) => {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await fetch(window.endpoints.phoneTracker, {
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                },
                redirect: 'follow',
                body: JSON.stringify({
                    trackingAccount,
                    trackingData,
                }),
            });
            if (!response.ok) {
                console.error('Response has a non-ok status');
                reject();
            }
            const data = await response.json();
            data.ptNumberId ? resolve(data) : reject('Response did not contain ptNumberId');
        }
        catch (error) {
            reject(error);
        }
    });
};
const compileTrackingData = async (gaClientId) => {
    const trackingHandler = TrackingHandler.getInstance();
    await trackingHandler.initialized;
    const trackingData = {
        data: trackingHandler.getAllTrackingData(),
    };
    if (trackingHandler.consent.functional) {
        trackingData.ga = compileGaTrackingData(gaClientId);
    }
    if (!window.siteSettings.enableSSGTM) {
        return trackingData;
    }
    const request = await ServerSideGtmListener.compileEvent({
        name: 'phonecall',
        time: '%callTimestamp%',
        count: -1,
        sessionCount: -1,
        sourceId: window.location.toString(),
        sourceType: 'web',
        previousSourceId: document.referrer || null,
        data: {
            classification: '%callClassification%',
            duration: '%callDuration%',
        },
    }, {
        serverFingerprints: {
            month: '%fingerprintMonth%',
            week: '%fingerprintWeek%',
            day: '%fingerprintDay%',
        },
    });
    trackingData.http = [
        {
            url: window.endpoints.tracking,
            method: request.method || 'POST',
            body: request.body || '',
            headers: Object.fromEntries(request.headers),
        },
    ];
    return trackingData;
};
const compileGaTrackingData = (clientId) => {
    return {
        clientId,
    };
};
const updateNumberData = async (ptNumberId, trackingData) => {
    return new Promise(async (resolve, reject) => {
        fetch(window.endpoints.phoneTracker, {
            method: 'PATCH',
            cache: 'no-cache',
            headers: {
                'Content-Type': 'application/json',
            },
            redirect: 'follow',
            body: JSON.stringify({
                ptNumberId,
                trackingData,
            }),
        })
            .then((response) => (response.ok ? resolve() : reject()))
            .catch(() => reject());
    });
};
const getNumberFromSession = (account) => {
    const numberDataJson = window.sessionStorage.getItem('ptNr_' + account);
    if (!numberDataJson) {
        return null;
    }
    return JSON.parse(numberDataJson);
};
const refreshNumberInSession = (account) => {
    const numberData = getNumberFromSession(account);
    if (numberData) {
        numberData.lastUpdated = Math.floor(new Date().getTime() / 1000);
        window.sessionStorage.setItem('ptNr_' + account, JSON.stringify(numberData));
    }
};
const setNumberInSession = (account, ptNumberId, phoneNumber) => {
    window.sessionStorage.setItem('ptNr_' + account, JSON.stringify({
        ptNumberId,
        phoneNumber,
        lastUpdated: Math.floor(new Date().getTime() / 1000),
    }));
};
const insertNumbers = (linkBlocks, number) => {
    for (const linkBlock of linkBlocks) {
        linkBlock.href = 'tel:' + number.replace(/ /g, '');
        const textBlock = linkBlock.querySelector('.c-link__text, .button__text');
        textBlock === null
            ? (linkBlock.innerText = formatDisplayNumber(number))
            : (textBlock.innerText = formatDisplayNumber(number));
    }
};
const setNumberInListener = (product, number) => {
    if (typeof window.phoneTrackingListener === 'undefined') {
        return;
    }
    window.phoneTrackingListener(product, number);
};
const initNumbers = async (selector, product, gaClientId) => {
    const account = window.phoneTrackingAccounts[product] || window.phoneTrackingAccounts['telephone'] || '';
    if (account.length === 0) {
        return;
    }
    const linkBlocks = document.querySelectorAll(selector);
    const hasListener = typeof window.phoneTrackingListener === 'function';
    if (linkBlocks.length === 0 && !hasListener) {
        return;
    }
    const trackingData = await compileTrackingData(gaClientId);
    const existingNumberData = getNumberFromSession(account);
    if (!existingNumberData) {
        createNewNumber(account, trackingData)
            .then((data) => {
            setNumberInSession(account, data.ptNumberId, data.phoneNumber);
            insertNumbers(linkBlocks, data.phoneNumber);
            setNumberInListener(product, data.phoneNumber);
        })
            .catch((e) => console.error(e));
        return;
    }
    if (existingNumberData.lastUpdated &&
        Math.floor(new Date().getTime() / 1000) - existingNumberData.lastUpdated < 3600) {
        insertNumbers(linkBlocks, existingNumberData.phoneNumber);
        setNumberInListener(product, existingNumberData.phoneNumber);
    }
    updateNumberData(existingNumberData.ptNumberId, trackingData)
        .then(() => {
        insertNumbers(linkBlocks, existingNumberData.phoneNumber);
        setNumberInListener(product, existingNumberData.phoneNumber);
        refreshNumberInSession(account);
    })
        .catch(() => {
        createNewNumber(account, trackingData)
            .then((data) => {
            setNumberInSession(account, data.ptNumberId, data.phoneNumber);
            insertNumbers(linkBlocks, data.phoneNumber);
            setNumberInListener(product, existingNumberData.phoneNumber);
        })
            .catch((e) => console.error(e));
    });
};
